import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bug, Paperclip, X, Loader2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { APP_VERSION } from "@/lib/version";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// "Probleme melden": contact form with optional screenshot, emailed to the platform admin's address.
export default function ProblemReportDialog({ open, onOpenChange, user }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setForm({ name: user?.name || "", email: user?.email || "", message: "" }); setFile(null); }
  }, [open, user]);
  useEffect(() => {
    if (!file) { setPreview(""); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Bitte ein Bild als Screenshot auswählen.");
    if (f.size > 10 * 1024 * 1024) return toast.error("Screenshot ist zu groß (max. 10 MB).");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!navigator.onLine) return toast.error("Senden geht nur mit Internetverbindung.");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("page", window.location.pathname);
      fd.append("app_version", APP_VERSION);
      if (file) fd.append("screenshot", file);
      const r = await api.post("/problem-reports", fd);
      if (r.data?.email_sent === false) toast.warning("Meldung gespeichert – die E-Mail konnte gerade nicht versendet werden.");
      else toast.success("Danke! Ihre Meldung wurde gesendet.");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Senden fehlgeschlagen"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="problem-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bug className="h-5 w-5 text-primary" />Probleme melden</DialogTitle>
          <DialogDescription>Beschreiben Sie den Fehler – wir kümmern uns darum.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label htmlFor="pr-name">Name</Label><Input id="pr-name" required value={form.name} onChange={set("name")} data-testid="problem-name-input" /></div>
            <div><Label htmlFor="pr-email">E-Mail</Label><Input id="pr-email" type="email" required value={form.email} onChange={set("email")} data-testid="problem-email-input" /></div>
          </div>
          <div>
            <Label htmlFor="pr-msg">Fehler / Problem schildern</Label>
            <Textarea id="pr-msg" required rows={6} maxLength={5000} value={form.message} onChange={set("message")}
              placeholder="Was ist passiert? Wo in der App? Was haben Sie erwartet?" data-testid="problem-message-input" />
          </div>
          <div>
            <Label>Screenshot (optional)</Label>
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="problem-screenshot-input" />
            {preview ? (
              <div className="relative mt-1 inline-block">
                <img src={preview} alt="Screenshot" className="max-h-40 rounded-lg border border-slate-200" data-testid="problem-screenshot-preview" />
                <button type="button" onClick={() => setFile(null)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center" data-testid="problem-screenshot-remove"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="mt-1 w-full" onClick={() => inputRef.current?.click()} data-testid="problem-screenshot-btn">
                <Paperclip className="h-4 w-4 mr-2" />Screenshot anhängen
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="problem-cancel-btn">Abbrechen</Button>
            <Button type="submit" disabled={busy} data-testid="problem-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Senden
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
